/**
 * Data Export Utility
 * Supports CSV, JSON, and PDF export for analytics data
 */

/**
 * Export data as CSV file
 * @param {Array} data - Array of objects to export
 * @param {string} filename - Output filename (without extension)
 */
export function exportCSV(data, filename = "analytics_export") {
  if (!data || data.length === 0) return;

  const headers = Object.keys(data[0]);
  const csvRows = [
    headers.join(","),
    ...data.map((row) =>
      headers
        .map((h) => {
          const val = row[h];
          const str = String(val ?? "");
          // Escape commas and quotes
          if (str.includes(",") || str.includes('"') || str.includes("\n")) {
            return `"${str.replace(/"/g, '""')}"`;
          }
          return str;
        })
        .join(",")
    ),
  ];

  const csv = csvRows.join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  downloadBlob(blob, `${filename}.csv`);
}

/**
 * Export data as JSON file
 * @param {Array|Object} data - Data to export
 * @param {string} filename - Output filename (without extension)
 */
export function exportJSON(data, filename = "analytics_export") {
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: "application/json" });
  downloadBlob(blob, `${filename}.json`);
}

/**
 * Export data as PDF file using jsPDF
 * @param {Array} data - Array of objects to export
 * @param {string} filename - Output filename (without extension)
 * @param {string} title - Report title
 */
export async function exportPDF(data, filename = "analytics_export", title = "Analytics Report") {
  const { jsPDF } = await import("jspdf");
  const doc = new jsPDF();

  // Title
  doc.setFontSize(18);
  doc.setTextColor(245, 158, 11); // amber-500
  doc.text(title, 14, 20);

  // Timestamp
  doc.setFontSize(9);
  doc.setTextColor(113, 113, 122); // zinc-500
  doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 28);

  if (!data || data.length === 0) {
    doc.setFontSize(12);
    doc.setTextColor(161, 161, 170);
    doc.text("No data available.", 14, 40);
    doc.save(`${filename}.pdf`);
    return;
  }

  // Table headers
  const headers = Object.keys(data[0]);
  const colWidth = Math.floor(180 / headers.length);
  let y = 40;

  doc.setFontSize(8);
  doc.setTextColor(245, 158, 11);
  headers.forEach((h, i) => {
    doc.text(String(h).toUpperCase(), 14 + i * colWidth, y);
  });

  // Table rows
  y += 6;
  doc.setFontSize(8);
  doc.setTextColor(212, 212, 216); // zinc-300

  data.forEach((row, rowIndex) => {
    if (y > 270) {
      doc.addPage();
      y = 20;
    }
    headers.forEach((h, i) => {
      const val = String(row[h] ?? "");
      const truncated = val.length > 20 ? val.substring(0, 18) + ".." : val;
      doc.text(truncated, 14 + i * colWidth, y);
    });
    y += 5;
  });

  doc.save(`${filename}.pdf`);
}

/**
 * Trigger download from a Blob
 */
function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Export chart data with period/channel context
 * @param {Array} data - Chart data
 * @param {string} chartName - Name of the chart
 * @param {string} format - 'csv', 'json', or 'pdf'
 * @param {Object} filters - { period, channel }
 */
export function exportChartData(data, chartName, format = "csv", filters = {}) {
  const timestamp = new Date().toISOString().split("T")[0];
  const filterSuffix = filters.period ? `_${filters.period}` : "";
  const channelSuffix = filters.channel && filters.channel !== "all" ? `_${filters.channel}` : "";
  const filename = `${chartName}${filterSuffix}${channelSuffix}_${timestamp}`;

  switch (format) {
    case "csv":
      exportCSV(data, filename);
      break;
    case "json":
      exportJSON(data, filename);
      break;
    case "pdf":
      exportPDF(data, filename, chartName);
      break;
    default:
      exportCSV(data, filename);
  }
}
